import { useCallback } from 'react';
import { useChatStore } from '../stores';
import { chatService } from '../services';

// AI联动事件名称
export const AI_LINKAGE_EVENT = 'ai-linkage:use-response';

export function useChat() {
  const {
    sessions,
    currentSessionId,
    isLoading,
    error,
    streamingContent,
    isStreaming,
    createSession,
    setCurrentSession,
    addMessage,
    deleteSession,
    deleteMessage,
    clearMessages,
    getCurrentSession,
    setLoading,
    setError,
    setIsStreaming,
    clearStreamingContent,
    appendStreamingContent,
  } = useChatStore();

  const currentSession = getCurrentSession();

  // Convert session messages to history format for API
  const getSessionHistory = useCallback(() => {
    if (!currentSession) return [];
    return currentSession.messages.map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));
  }, [currentSession]);

  const sendMessage = useCallback(
    async (content: string) => {
      // Create session if none exists
      let sessionId = currentSessionId;
      if (!sessionId) {
        sessionId = createSession();
      }

      // Add user message
      addMessage(sessionId, { role: 'user', content });

      setLoading(true);
      setError(null);
      setIsStreaming(true);
      clearStreamingContent();

      try {
        // Get current history
        const history = getSessionHistory();

        // Call API with streaming
        const result = await chatService.streamBusinessMessage(
          content,
          history,
          (chunk) => {
            appendStreamingContent(chunk);
          }
        );

        // Add assistant message with full response
        addMessage(sessionId, { role: 'assistant', content: result.response });
        clearStreamingContent();

        // AI联动：保存AI回复到store
        const { setLastAIResponse } = useChatStore.getState();
        console.log('[useChat] Setting lastAIResponse, length:', result.response.length);
        setLastAIResponse(result.response);

        // Store config if available (could be used by other components)
        if (result.config) {
          console.log('[useChat] Received config:', result.config);
          // You can emit an event or update a separate store here
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '发送消息失败';
        setError(errorMessage);

        // Add error message
        addMessage(sessionId, {
          role: 'assistant',
          content: `抱歉，发生了错误：${errorMessage}。请稍后重试。`,
        });
        console.error('Chat error:', err);
      } finally {
        setLoading(false);
        setIsStreaming(false);
      }
    },
    [
      currentSessionId,
      createSession,
      addMessage,
      getSessionHistory,
      setLoading,
      setError,
      setIsStreaming,
      clearStreamingContent,
      appendStreamingContent,
    ]
  );

  const sendMessageNonStream = useCallback(
    async (content: string) => {
      let sessionId = currentSessionId;
      if (!sessionId) {
        sessionId = createSession();
      }

      addMessage(sessionId, { role: 'user', content });
      setLoading(true);
      setError(null);

      try {
        const history = getSessionHistory();
        const response = await chatService.sendBusinessMessage(content, history);

        if (response.success && response.data) {
          addMessage(sessionId, {
            role: 'assistant',
            content: response.data.response,
          });

          // AI联动：保存AI回复到store
          const { setLastAIResponse } = useChatStore.getState();
          setLastAIResponse(response.data.response);

          if (response.data.config) {
            console.log('[useChat] Received config:', response.data.config);
          }
        } else {
          throw new Error(response.error || '未知错误');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '发送消息失败';
        setError(errorMessage);
        addMessage(sessionId, {
          role: 'assistant',
          content: `抱歉，发生了错误：${errorMessage}。请稍后重试。`,
        });
        console.error('Chat error:', err);
      } finally {
        setLoading(false);
      }
    },
    [currentSessionId, createSession, addMessage, getSessionHistory, setLoading, setError]
  );

  const regenerateMessage = useCallback(
    async (messageId: string) => {
      if (!currentSession) return;

      const messageIndex = currentSession.messages.findIndex(
        (m) => m.id === messageId
      );
      if (messageIndex <= 0) return;

      const userMessage = currentSession.messages[messageIndex - 1];
      if (userMessage.role !== 'user') return;

      // Remove the assistant message we're regenerating
      deleteMessage(currentSession.id, messageId);

      setLoading(true);
      setIsStreaming(true);
      clearStreamingContent();

      try {
        // Get history up to the user message
        const history = currentSession.messages
          .slice(0, messageIndex - 1)
          .map((msg) => ({
            role: msg.role,
            content: msg.content,
          }));

        const result = await chatService.streamBusinessMessage(
          userMessage.content,
          history,
          (chunk) => {
            appendStreamingContent(chunk);
          }
        );

        addMessage(currentSession.id, {
          role: 'assistant',
          content: result.response,
        });
        clearStreamingContent();

        // AI联动：保存AI回复到store
        const { setLastAIResponse } = useChatStore.getState();
        setLastAIResponse(result.response);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '重新生成失败';
        setError(errorMessage);
        addMessage(currentSession.id, {
          role: 'assistant',
          content: `抱歉，重新生成失败：${errorMessage}。请稍后重试。`,
        });
      } finally {
        setLoading(false);
        setIsStreaming(false);
      }
    },
    [
      currentSession,
      deleteMessage,
      addMessage,
      setLoading,
      setError,
      setIsStreaming,
      clearStreamingContent,
      appendStreamingContent,
    ]
  );

  const clearCurrentSession = useCallback(() => {
    if (currentSessionId) {
      clearMessages(currentSessionId);
    }
  }, [currentSessionId, clearMessages]);

  return {
    // State
    sessions,
    currentSession,
    currentSessionId,
    isLoading,
    error,
    streamingContent,
    isStreaming,

    // Actions
    createSession,
    setCurrentSession,
    sendMessage,
    sendMessageNonStream,
    deleteSession,
    deleteMessage,
    clearCurrentSession,
    regenerateMessage,
  };
}
